import React, { useState } from "react";
import { Box, Typography, CircularProgress, Grid } from "@mui/material";
import { useAuth } from "react-oidc-context";
import CheckIcon from "@mui/icons-material/Check";
import LockIcon from "@mui/icons-material/Lock";
import logo from "../../../../assets/kloud-web-logo-blue.svg";

// ─── Prices ───────────────────────────────────────────────────────────────────
const PRICES = {
  monthly: { amount: 13.75, price_id: "price_1TJmAhJcvMflQ4Co0oi4rwVf" },
  annual: { amount: 99, price_id: "price_1TJmDKJcvMflQ4Cot4yYRhva" },
};

const MONTHLY_EQUIV = (PRICES.annual.amount / 12).toFixed(2);
const SAVINGS_AMT = Math.round(
  PRICES.monthly.amount * 12 - PRICES.annual.amount,
);

const FEATURES = [
  "25+ data broker & people-search sites removed",
  "Real-time dashboard & request tracking",
  "Continuous monitoring & re-removal requests",
];

const TRUST_BADGES = [
  { icon: "↩", label: "30-day money back guarantee" },
  { icon: "⚡", label: "Cancel anytime" },
];

// ─── Font load ────────────────────────────────────────────────────────────────
(() => {
  if (typeof document === "undefined") return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap";
  document.head.appendChild(link);
})();

// ─── Tokens ───────────────────────────────────────────────────────────────────
const cyan = "#12b2f1";
const orange = "#f5a623";
const orangeHov = "#e09410";
const ink = "#0d1117";
const muted = "#6B7280";
const green = "#16a34a";
const border = "#E5E7EB";

// ─── Plan Card subcomponent ───────────────────────────────────────────────────
const PlanCard = ({ active, onClick, children }) => (
  <Box
    onClick={onClick}
    sx={{
      border: `2px solid ${active ? cyan : border}`,
      borderRadius: "12px",
      p: { xs: 1.75, sm: 2 },
      cursor: "pointer",
      bgcolor: active ? "rgba(18,178,241,0.04)" : "#fff",
      transition: "all 0.18s",
      display: "flex",
      alignItems: "center",
      gap: 1.5,
    }}
  >
    {/* Inline radio circle */}
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        border: `2px solid ${active ? cyan : "#D1D5DB"}`,
        bgcolor: active ? cyan : "#fff",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.18s",
      }}
    >
      {active && (
        <Box
          sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#fff" }}
        />
      )}
    </Box>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────
const Checkout = () => {
  const [billing, setBilling] = useState("annual");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const auth = useAuth();
  const { user } = auth;

  const selected = PRICES[billing];

  const handleContinue = async () => {
    setLoading(true);
    setError("");
    try {
      const token = user?.access_token;
      console.log("token:", token);
      console.log("email:", user?.profile?.email);

      const res = await fetch("https://api.kloudscan.com/stripe_checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          line_items: [{ price: selected.price_id, quantity: 1 }],
          customer_email: user?.profile?.email,
        }),
      });

      console.log("status:", res.status);
      const data = await res.json();
      console.log("response:", data);

      if (res.status === 400) {
        setError(data.error || "You are already subscribed.");
        return;
      }

      if (data.url) window.location.href = data.url;
      else setError("Unable to start checkout. Please try again.");
    } catch (err) {
      console.error("fetch error:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f7f8fa",
        fontFamily: "'Sora', sans-serif",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: { xs: "flex-start", md: "center" },
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 5 },
        boxSizing: "border-box",
      }}
    >
      {/* ── Logo ── */}
      <Box
        sx={{
          mb: { xs: 3, sm: 4 },
          width: "100%",
          maxWidth: 860,
          textAlign: { xs: "center", md: "center" },
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="KloudScan"
          sx={{ height: 54, display: "inline-block" }}
        />
      </Box>

      {/* ── Card ── */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 860,
          bgcolor: "#fff",
          borderRadius: { xs: "16px", sm: "20px" },
          border: `1.5px solid ${border}`,
          boxShadow: "0 8px 40px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        <Grid container>
          {/* ── LEFT: features ── */}
          <Grid
            size={{ xs: 12, md: 7 }}
            sx={{
              p: { xs: 3, sm: 4 },
              borderRight: { md: `1.5px solid ${border}` },
              borderBottom: { xs: `1.5px solid ${border}`, md: "none" },
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Sora', sans-serif",
                fontWeight: 800,
                fontSize: { xs: 20, sm: 24 },
                color: ink,
                lineHeight: 1.25,
                mb: 1,
              }}
            >
              Welcome to KloudScan.{" "}
              <Box component="span" sx={{ color: cyan }}>
                Let's get you protected.
              </Box>
            </Typography>

            <Typography
              sx={{ fontSize: 14, color: muted, lineHeight: 1.6, mb: 3 }}
            >
              Choose a plan to get started. We'll walk you through everything
              else once you're in.
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                mb: 3,
              }}
            >
              {FEATURES.map((f, i) => (
                <Box
                  key={i}
                  sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}
                >
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      bgcolor: "rgba(18,178,241,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      mt: "1px",
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 12, color: cyan }} />
                  </Box>
                  <Typography
                    sx={{ fontSize: 14, color: "#374151", lineHeight: 1.55 }}
                  >
                    {f}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: { xs: 2, sm: 3 },
                flexWrap: "wrap",
                pt: 2,
                borderTop: `1px solid ${border}`,
              }}
            >
              {TRUST_BADGES.map((b, i) => (
                <Box
                  key={i}
                  sx={{ display: "flex", alignItems: "center", gap: 0.6 }}
                >
                  <Typography sx={{ fontSize: 13 }}>{b.icon}</Typography>
                  <Typography
                    sx={{ fontSize: 12, color: muted, fontWeight: 600 }}
                  >
                    {b.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* ── RIGHT: plan picker + CTA ── */}
          <Grid
            size={{ xs: 12, md: 5 }}
            sx={{
              p: { xs: 3, sm: 4 },
              bgcolor: "#FAFBFC",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Sora', sans-serif",
                fontWeight: 700,
                fontSize: 12,
                color: muted,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
              }}
            >
              Choose your plan
            </Typography>

            {/* Annual */}
            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  position: "absolute",
                  top: -10,
                  right: 12,
                  bgcolor: cyan,
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 800,
                  px: 1.25,
                  py: 0.3,
                  borderRadius: "5px",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  zIndex: 1,
                }}
              >
                Best value
              </Box>
              <PlanCard
                active={billing === "annual"}
                onClick={() => setBilling("annual")}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{ fontWeight: 700, fontSize: 14, color: ink }}
                    >
                      Annual
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: green,
                        fontWeight: 600,
                        mt: 0.25,
                      }}
                    >
                      Save ${SAVINGS_AMT}/year
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: 18,
                        color: ink,
                        lineHeight: 1,
                      }}
                    >
                      ${MONTHLY_EQUIV}
                      <Box
                        component="span"
                        sx={{ fontSize: 11, fontWeight: 500, color: muted }}
                      >
                        /mo
                      </Box>
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: muted, mt: 0.25 }}>
                      ${PRICES.annual.amount}/yr
                    </Typography>
                  </Box>
                </Box>
              </PlanCard>
            </Box>

            {/* Monthly */}
            <PlanCard
              active={billing === "monthly"}
              onClick={() => setBilling("monthly")}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography
                    sx={{ fontWeight: 700, fontSize: 14, color: ink }}
                  >
                    Monthly
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: muted, mt: 0.25 }}>
                    Flexible, month-to-month
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: 18,
                      color: ink,
                      lineHeight: 1,
                    }}
                  >
                    ${PRICES.monthly.amount}
                    <Box
                      component="span"
                      sx={{ fontSize: 11, fontWeight: 500, color: muted }}
                    >
                      /mo
                    </Box>
                  </Typography>
                </Box>
              </Box>
            </PlanCard>

            {/* Due today */}
            <Box
              sx={{
                bgcolor: "#F3F4F6",
                borderRadius: "10px",
                px: 2,
                py: 1.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontSize: 13, color: muted, fontWeight: 600 }}>
                Due today
              </Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: ink }}>
                {billing === "annual"
                  ? `$${PRICES.annual.amount}/yr`
                  : `$${PRICES.monthly.amount}/mo`}
              </Typography>
            </Box>

            {error && (
              <Typography
                sx={{ fontSize: 13, color: "#dc2626", textAlign: "center" }}
              >
                {error}
              </Typography>
            )}

            {/* CTA */}
            <Box
              component="button"
              onClick={handleContinue}
              disabled={loading}
              sx={{
                width: "100%",
                py: 1.85,
                bgcolor: loading ? "#E5E7EB" : orange,
                color: loading ? muted : "#000",
                fontFamily: "'Sora', sans-serif",
                fontWeight: 800,
                fontSize: 15,
                border: "none",
                borderRadius: "11px",
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                transition: "background 0.2s",
                "&:hover": { bgcolor: loading ? "#E5E7EB" : orangeHov },
              }}
            >
              {loading ? (
                <CircularProgress size={18} sx={{ color: muted }} />
              ) : (
                <>
                  Continue to payment
                  <Box component="span" sx={{ fontSize: 16 }}>
                    →
                  </Box>
                </>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
              }}
            >
              <LockIcon sx={{ fontSize: 12, color: muted }} />
              <Typography sx={{ fontSize: 11, color: muted }}>
                Secure checkout · Powered by Stripe
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Typography
        sx={{
          mt: { xs: 2, sm: 3 },
          fontSize: 12,
          color: "#9CA3AF",
          textAlign: "center",
        }}
      >
        You won't be charged until you complete payment on the next screen.
      </Typography>
    </Box>
  );
};

export default Checkout;
